/**
 * Spintax v2 Processing Engine
 * Core logic for converting, processing, and generating variations
 * 
 * This module provides all the core functionality for Spintax processing:
 * - Detection of existing Spintax syntax
 * - Conversion of plain text to Spintax format
 * - Processing Spintax into resolved text
 * - Generation of multiple unique variations
 * 
 * @module spintax-v2/engine
 * @version 2.0.0
 */

// ES6 imports (must be at top for CRA)
import { SPINTAX_CONFIG } from './config.js';
import { synonymMap, getSortedKeys } from './synonymMap.js';

/**
 * Escape regex special characters in a string
 * @private
 * @param {string} string - String to escape
 * @returns {string} Escaped string safe for regex
 */
const escapeRegExp = (string) => {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

/**
 * Log debug message if debug mode is enabled
 * @private
 * @param {string} message - Message to log
 * @param {*} data - Optional data to include
 */
const debugLog = (message, data = null) => {
  if (SPINTAX_CONFIG && SPINTAX_CONFIG.DEBUG_MODE) {
    const prefix = SPINTAX_CONFIG.LOG_PREFIX || '[Spintax v2]';
    if (data !== null) {
      console.log(`${prefix} ${message}`, data);
    } else {
      console.log(`${prefix} ${message}`);
    }
  }
};

/**
 * Detect if text contains Spintax syntax
 * 
 * @param {string} text - Text to analyze
 * @returns {Object} Detection results
 * @returns {boolean} returns.hasSpintax - Whether Spintax syntax was found
 * @returns {number} returns.syntaxCount - Number of Spintax groups found
 * @returns {string[]} returns.matches - Array of matched Spintax groups
 * 
 * @example
 * detectSpintax("Hi, I want to {book|schedule} a venue")
 * // Returns: { hasSpintax: true, syntaxCount: 1, matches: ["{book|schedule}"] }
 */
export const detectSpintax = (text) => {
  if (!text) {
    return { hasSpintax: false, syntaxCount: 0, matches: [] };
  }
  
  const matches = text.match(/\{[^{}]*\|[^{}]*\}/g);
  
  const result = {
    hasSpintax: !!matches,
    syntaxCount: matches ? matches.length : 0,
    matches: matches || []
  };
  
  debugLog('Spintax detection:', result);
  
  return result;
};

/**
 * Convert plain text to Spintax format using synonymMap
 * 
 * @param {string} text - Plain text to convert
 * @returns {string} Text with Spintax syntax applied
 * 
 * @example
 * convertToSpintax("Hi, I want to book")
 * // Returns: "{Hi|Hello|Hey}, I {want|would like} to {book|schedule}"
 */
export const convertToSpintax = (text) => {
  if (!text) return text;
  
  const startTime = SPINTAX_CONFIG && SPINTAX_CONFIG.TRACK_PERFORMANCE ? Date.now() : null;
  
  let result = text;
  
  // Get sorted keys (longest first to match phrases before words)
  const sortedKeys = getSortedKeys ? getSortedKeys() : Object.keys(synonymMap).sort((a, b) => b.length - a.length);
  
  debugLog(`Converting text with ${sortedKeys.length} synonym entries`);
  
  for (const key of sortedKeys) {
    const caseSensitive = SPINTAX_CONFIG ? SPINTAX_CONFIG.CASE_SENSITIVE : false;
    
    // ✅ Create regex that SKIPS words already inside spintax braces
    // This prevents nested spintax like {{word|...}|...} or {word|{other|...}}
    const pattern = `(?<!\\w)(?<!\\{[^}]{0,200})${escapeRegExp(key)}(?![^{]{0,200}\\})(?!\\w)`;
    const regex = new RegExp(pattern, caseSensitive ? 'g' : 'gi');
    
    result = result.replace(regex, (match) => {
      // ✅ Use synonymMap directly (already includes the original word as first option)
      const variations = synonymMap[key];
      return `{${variations.join('|')}}`;
    });
  }
  
  if (startTime) {
    const duration = Date.now() - startTime;
    debugLog(`Conversion completed in ${duration}ms`);
  }
  
  return result;
};

/**
 * Process Spintax: resolve {opt1|opt2|opt3} to ONE random choice
 * Supports nested Spintax by processing recursively
 * 
 * @param {string} text - Text containing Spintax syntax
 * @returns {string} Text with all Spintax resolved to random choices
 * 
 * @example
 * processSpintax("{Hi|Hello}, {book|schedule}")
 * // Returns: "Hello, schedule" (or any other random combination)
 */
export const processSpintax = (text) => {
  if (!text) return text;
  
  const trimOptions = SPINTAX_CONFIG ? SPINTAX_CONFIG.TRIM_OPTIONS : true;
  let result = text;
  let previousResult;
  let iterations = 0;
  const maxIterations = 10; // Prevent infinite loops
  
  // Keep processing until no more Spintax syntax remains
  do {
    previousResult = result;
    result = result.replace(/\{([^{}]*)\}/g, (_, group) => {
      const options = group.split('|').map(opt => trimOptions ? opt.trim() : opt);
      const randomIndex = Math.floor(Math.random() * options.length);
      return options[randomIndex];
    });
    iterations++;
  } while (result !== previousResult && iterations < maxIterations);
  
  if (iterations >= maxIterations) {
    console.warn('🌀 [Spintax v2] Warning: Maximum iterations reached during processing. Text may still contain Spintax syntax.');
  }
  
  return result;
};

/**
 * Generate multiple unique variations from Spintax text
 * 
 * @param {string} text - Text containing Spintax syntax
 * @param {number} [count] - Number of variations to generate (default from config)
 * @returns {string[]} Array of resolved variations
 * 
 * @example
 * generateSpintaxVariations("{Hi|Hello}, {book|schedule}", 3)
 * // Returns: ["Hi, book", "Hello, schedule", "Hi, schedule"]
 */
export const generateSpintaxVariations = (text, count) => {
  if (!text) return [];
  
  const config = SPINTAX_CONFIG || {};
  const targetCount = count !== undefined ? count : (config.DEFAULT_VARIATION_COUNT || 5);
  
  // Clamp count to valid range
  const minCount = config.MIN_VARIATION_COUNT || 3;
  const maxCount = config.MAX_VARIATION_COUNT || 10;
  const clampedCount = Math.max(minCount, Math.min(targetCount, maxCount));
  
  const variations = new Set();
  let attempts = 0;
  const maxAttempts = config.MAX_ATTEMPTS || 20;
  
  debugLog(`Generating ${clampedCount} variations (max attempts: ${maxAttempts})`);
  
  while (variations.size < clampedCount && attempts < maxAttempts) {
    const variation = processSpintax(text);
    variations.add(variation);
    attempts++;
  }
  
  const result = Array.from(variations);
  
  // Log warning if we couldn't generate enough unique variations
  if (result.length < clampedCount) {
    const message = `Could only generate ${result.length}/${clampedCount} unique variations after ${attempts} attempts`;
    debugLog(message);
    
    if (console.warn) {
      console.warn(`🌀 [Spintax v2] ${message}`);
    }
  } else {
    debugLog(`Successfully generated ${result.length} unique variations in ${attempts} attempts`);
  }
  
  return result;
};

/**
 * Generate variations with detailed metadata
 * V2 ONLY - provides rich information about the generation process
 * 
 * @param {string} text - Text containing Spintax syntax
 * @param {number} [count] - Number of variations to generate
 * @returns {Object} Generation results with metadata
 * @returns {string[]} returns.variations - Array of resolved variations
 * @returns {Object} returns.metadata - Detailed generation metadata
 * 
 * @example
 * const result = generateVariationsWithMetadata("{Hi|Hello}, I want to {book|schedule}", 5);
 * console.log(result.variations); // ["Hi, I want to book", ...]
 * console.log(result.metadata);   // { originalText: "...", hasSpintax: true, ... }
 */
export const generateVariationsWithMetadata = (text, count) => {
  const startTime = Date.now();
  
  const detection = detectSpintax(text);
  const variations = generateSpintaxVariations(text, count);
  
  const duration = Date.now() - startTime;
  
  const config = SPINTAX_CONFIG || {};
  const requestedCount = count !== undefined ? count : (config.DEFAULT_VARIATION_COUNT || 5);
  
  return {
    variations,
    metadata: {
      originalText: text,
      hasSpintax: detection.hasSpintax,
      syntaxCount: detection.syntaxCount,
      syntaxMatches: detection.matches,
      generatedCount: variations.length,
      requestedCount: requestedCount,
      uniquenessRatio: variations.length / requestedCount,
      processingTime: duration,
      timestamp: new Date().toISOString(),
      version: config.VERSION || '2.0.0'
    }
  };
};

/**
 * Full pipeline: convert plain text to resolved variations
 * This is the main function for campaign creation
 * 
 * @param {string} plainText - Plain text or text with existing Spintax
 * @param {number} [count] - Number of variations to generate
 * @returns {Object} Conversion results
 * @returns {boolean} returns.success - Whether conversion succeeded
 * @returns {string} returns.spintaxText - Text with Spintax syntax
 * @returns {string[]} returns.variations - Array of resolved variations
 * @returns {Object} returns.metadata - Conversion metadata
 * @returns {string} [returns.error] - Error message if failed
 * 
 * @example
 * const result = convertAndGenerate("Hi, I want to book a venue", 5);
 * if (result.success) {
 *   console.log(result.variations); // Array of 5 unique variations
 * }
 */
export const convertAndGenerate = (plainText, count) => {
  if (!plainText) {
    return {
      success: false,
      error: 'No text provided',
      variations: [],
      metadata: {}
    };
  }
  
  try {
    const startTime = Date.now();
    
    // Step 1: Check if already contains Spintax
    const detection = detectSpintax(plainText);
    
    debugLog('Input analysis:', {
      length: plainText.length,
      hasSpintax: detection.hasSpintax,
      syntaxCount: detection.syntaxCount
    });
    
    let spintaxText;
    if (detection.hasSpintax) {
      // Already has Spintax syntax - use directly
      spintaxText = plainText;
      debugLog('Using existing Spintax syntax');
    } else {
      // Convert plain text to Spintax
      spintaxText = convertToSpintax(plainText);
      debugLog('Converted plain text to Spintax');
    }
    
    // Step 2: Generate variations
    const variations = generateSpintaxVariations(spintaxText, count);
    
    const duration = Date.now() - startTime;
    
    const config = SPINTAX_CONFIG || {};
    const requestedCount = count !== undefined ? count : (config.DEFAULT_VARIATION_COUNT || 5);
    
    debugLog('Conversion complete:', {
      inputType: detection.hasSpintax ? 'spintax' : 'plain',
      variationsGenerated: variations.length,
      duration: `${duration}ms`
    });
    
    return {
      success: true,
      spintaxText,
      variations,
      metadata: {
        inputType: detection.hasSpintax ? 'spintax' : 'plain',
        syntaxCount: detection.hasSpintax ? detection.syntaxCount : 0,
        generatedCount: variations.length,
        requestedCount: requestedCount,
        uniquenessRatio: variations.length / requestedCount,
        processingTime: duration,
        timestamp: new Date().toISOString(),
        version: config.VERSION || '2.0.0'
      }
    };
  } catch (error) {
    console.error('🌀 [Spintax v2] Conversion error:', error);
    
    return {
      success: false,
      error: error.message || 'Unknown error during conversion',
      variations: [],
      metadata: {
        errorType: error.name,
        timestamp: new Date().toISOString()
      }
    };
  }
};

/**
 * Preview Spintax text (show both raw and processed)
 * Useful for debugging and UI display
 * 
 * @param {string} text - Text to preview
 * @param {number} [count=3] - Number of example variations to show
 * @returns {Object} Preview data
 */
export const previewSpintax = (text, count = 3) => {
  const detection = detectSpintax(text);
  const examples = detection.hasSpintax ? generateSpintaxVariations(text, count) : [];
  
  return {
    rawText: text,
    hasSpintax: detection.hasSpintax,
    syntaxCount: detection.syntaxCount,
    examples: examples,
    exampleCount: examples.length
  };
};

// Export for CommonJS (Node.js)
if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    detectSpintax,
    convertToSpintax,
    processSpintax,
    generateSpintaxVariations,
    generateVariationsWithMetadata,
    convertAndGenerate,
    previewSpintax
  };
}

// Default export for ES6
export default {
  detectSpintax,
  convertToSpintax,
  processSpintax,
  generateSpintaxVariations,
  generateVariationsWithMetadata,
  convertAndGenerate,
  previewSpintax
};

